import React, { useState, useMemo, useCallback } from 'react';
import {
  Clock, Plus, Trash2, GripVertical, ChevronDown, ChevronUp,
  AlertTriangle, Sparkles, RefreshCw, ArrowUp, ArrowDown
} from 'lucide-react';
import { Button } from '../ui/button';

const DEFAULT_EVENTS = [
  { id: 'getting-ready', label: 'Getting Ready', category: 'prep', defaultDuration: 120, buffer: 15 },
  { id: 'beauty-hair', label: 'Hair & Makeup', category: 'prep', defaultDuration: 90, buffer: 10 },
  { id: 'first-look', label: 'First Look (optional)', category: 'prep', defaultDuration: 20, buffer: 10 },
  { id: 'photographer-prep', label: 'Photographer — forberedelsesbilder', category: 'prep', defaultDuration: 30, buffer: 10 },
  { id: 'transport-ceremony', label: 'Transport til seremoni', category: 'transport', defaultDuration: 30, buffer: 15 },
  { id: 'guest-arrival', label: 'Guest arrival', category: 'ceremony', defaultDuration: 30, buffer: 5 },
  { id: 'ceremony', label: 'Ceremony', category: 'ceremony', defaultDuration: 45, buffer: 10 },
  { id: 'group-photos', label: 'Group photos', category: 'photos', defaultDuration: 30, buffer: 10 },
  { id: 'couple-photos', label: 'Parbilder', category: 'photos', defaultDuration: 30, buffer: 10 },
  { id: 'cocktail-hour', label: 'Cocktail Hour', category: 'reception', defaultDuration: 60, buffer: 5 },
  { id: 'dinner-seating', label: 'Guests finner plassene', category: 'reception', defaultDuration: 15, buffer: 0 },
  { id: 'welcome-speech', label: 'Welcome speech', category: 'reception', defaultDuration: 10, buffer: 5 },
  { id: 'dinner', label: 'Middag', category: 'reception', defaultDuration: 90, buffer: 5 },
  { id: 'speeches', label: 'Speeches', category: 'reception', defaultDuration: 30, buffer: 5 },
  { id: 'cake-cutting', label: 'Cake cutting', category: 'reception', defaultDuration: 15, buffer: 5 },
  { id: 'first-dance', label: 'First dance', category: 'party', defaultDuration: 5, buffer: 0 },
  { id: 'party', label: 'Fest & dans', category: 'party', defaultDuration: 120, buffer: 0 },
  { id: 'activities', label: 'Leker / aktiviteter', category: 'party', defaultDuration: 30, buffer: 5 },
  { id: 'last-call', label: 'Last call', category: 'wrapup', defaultDuration: 15, buffer: 0 },
  { id: 'transport-home', label: 'Transport hjem / hotell', category: 'wrapup', defaultDuration: 30, buffer: 0 },
  { id: 'cleanup', label: 'Rydding & nedrigging', category: 'wrapup', defaultDuration: 60, buffer: 0 },
];

const CATEGORIES = {
  prep: { label: 'Preparations', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  transport: { label: 'Transport', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  ceremony: { label: 'Ceremony', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  photos: { label: 'Bilder', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  reception: { label: 'Mottakelse', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  party: { label: 'Fest', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  wrapup: { label: 'Avslutning', color: 'bg-stone-100 text-stone-600 border-stone-200' },
  custom: { label: 'Egendefinert', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
};

const formatTime = (minutes) => {
  if (minutes < 0) return '--:--';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

const parseTime = (str) => {
  if (!str) return null;
  const [h, m] = str.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

const defaultTimelineEvent = (template = null) => ({
  id: `ev-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
  label: template?.label || '',
  category: template?.category || 'custom',
  duration: template?.defaultDuration || 30,
  buffer: template?.buffer ?? 10,
  startTime: null,
  responsible: '',
  notes: '',
  locked: false,
});

export const WeddingTimeline = ({ timeline, onChange, formData, templateData }) => {
  const [expanded, setExpanded] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const events = timeline?.events || [];
  const ceremonyTime = useMemo(() => parseTime(formData?.weddingTime), [formData?.weddingTime]);

  const updateEvents = useCallback((newEvents) => {
    onChange({ ...timeline, events: newEvents });
  }, [timeline, onChange]);

  const autoGenerate = () => {
    const base = DEFAULT_EVENTS
      .filter(e => {
        if (e.id === 'activities' && !(templateData?.music?.activities?.length > 0)) return true;
        return true;
      })
      .map(t => defaultTimelineEvent(t));

    // Auto-assign times from ceremony start
    if (ceremonyTime) {
      let current = ceremonyTime;
      // Find ceremony event index
      const ceremonyIdx = base.findIndex(e => e.id.startsWith('ev-') ? false : e.category === 'ceremony' && e.label === 'Ceremony');
      // Work backwards from ceremony for prep
      if (ceremonyIdx >= 0) {
        let backward = ceremonyTime;
        for (let i = ceremonyIdx - 1; i >= 0; i--) {
          backward -= base[i].duration + base[i].buffer;
          base[i].startTime = formatTime(Math.max(0, backward));
        }
        // Forward from ceremony
        current = ceremonyTime;
        for (let i = ceremonyIdx; i < base.length; i++) {
          base[i].startTime = formatTime(current);
          current += base[i].duration + base[i].buffer;
        }
      }
    }

    updateEvents(base);
  };

  const recalculateTimes = () => {
    if (!ceremonyTime || events.length === 0) return;
    const updated = [...events];
    const ceremonyIdx = updated.findIndex(e => e.label === 'Ceremony' || e.label === 'ceremony');

    if (ceremonyIdx >= 0) {
      let backward = ceremonyTime;
      for (let i = ceremonyIdx - 1; i >= 0; i--) {
        if (!updated[i].locked) {
          backward -= updated[i].duration + updated[i].buffer;
          updated[i].startTime = formatTime(Math.max(0, backward));
        } else {
          backward = parseTime(updated[i].startTime) || backward;
        }
      }
      let current = ceremonyTime;
      for (let i = ceremonyIdx; i < updated.length; i++) {
        if (!updated[i].locked) {
          updated[i].startTime = formatTime(current);
        }
        current = (parseTime(updated[i].startTime) || current) + updated[i].duration + updated[i].buffer;
      }
    }
    updateEvents(updated);
  };

  const addEvent = () => {
    updateEvents([...events, defaultTimelineEvent()]);
    setExpanded(events.length);
  };

  const removeEvent = (idx) => updateEvents(events.filter((_, i) => i !== idx));

  const updateEvent = (idx, field, value) => {
    const updated = [...events];
    updated[idx] = { ...updated[idx], [field]: value };
    updateEvents(updated);
  };

  const moveEvent = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= events.length) return;
    const updated = [...events];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    updateEvents(updated);
  };

  // Cross-module suggestions
  const suggestions = useMemo(() => {
    const s = [];
    if (templateData?.attire?.beautySchedule?.length > 0 && !events.some(e => e.label.includes('Hair') || e.label.includes('Makeup'))) {
      s.push('Beauty appointments found in Attire — consider adding them to the timeline');
    }
    if (templateData?.food?.cateringType && !events.some(e => e.label.includes('Middag') || e.label.includes('Buffet'))) {
      s.push('Catering is planned but dinner is not on the timeline');
    }
    if ((templateData?.memorials || []).some(m => m.tributes?.includes('dedicated-song') || m.tributes?.includes('silent-moment'))) {
      s.push('Memorial is planned — remember to add the time to the timeline');
    }
    return s;
  }, [templateData, events]);

  const totalDuration = events.reduce((s, e) => s + (e.duration || 0) + (e.buffer || 0), 0);
  const firstStart = events[0]?.startTime;
  const lastEnd = events.length > 0 ? formatTime((parseTime(events[events.length - 1]?.startTime) || 0) + (events[events.length - 1]?.duration || 0)) : null;

  return (
    <div data-testid="wedding-timeline" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-sans font-semibold text-stone-800 text-sm">Wedding Day Timeline</h3>
          <p className="text-xs font-sans text-stone-500 mt-0.5">Din minutt-for-minutt plan for weddingdagen</p>
          <p className="text-[10px] font-sans text-stone-400 mt-1 leading-relaxed">
            Click 'Auto-generate' to create a complete timeline from the ceremony time. Adjust durations, lock fixed times, and sort freely.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {events.length > 0 && (
            <Button type="button" variant="outline" size="sm" onClick={recalculateTimes}
              className="rounded-full text-[10px] font-sans gap-1 border-stone-200" data-testid="recalculate-times-btn">
              <RefreshCw className="w-3 h-3" /> Rekalkuler
            </Button>
          )}
          <Button type="button" onClick={autoGenerate} size="sm"
            className="bg-primary hover:bg-primary/90 text-white rounded-full text-[10px] font-sans gap-1" data-testid="auto-generate-timeline-btn">
            <Sparkles className="w-3 h-3" /> {events.length > 0 ? 'Regenerate' : 'Auto-generate timeline'}
          </Button>
        </div>
      </div>

      {/* Stats bar */}
      {events.length > 0 && (
        <div className="flex items-center gap-4 bg-white rounded-xl border border-stone-100 p-3">
          <div>
            <p className="text-[10px] font-sans text-stone-400 uppercase">Start</p>
            <p className="text-sm font-sans font-bold text-stone-700">{firstStart || '--:--'}</p>
          </div>
          <div className="flex-1 h-1.5 bg-stone-100 rounded-full relative">
            {Object.entries(CATEGORIES).map(([key, cfg]) => {
              const catEvents = events.filter(e => e.category === key);
              const catDuration = catEvents.reduce((s, e) => s + e.duration, 0);
              const pct = totalDuration > 0 ? (catDuration / totalDuration) * 100 : 0;
              return pct > 0 ? <div key={key} title={`${cfg.label}: ${catDuration} min`} className={`h-full ${cfg.color.split(' ')[0]} inline-block rounded-full`} style={{ width: `${pct}%` }} /> : null;
            })}
          </div>
          <div>
            <p className="text-[10px] font-sans text-stone-400 uppercase">Slutt</p>
            <p className="text-sm font-sans font-bold text-stone-700">{lastEnd || '--:--'}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-sans text-stone-400 uppercase">Totalt</p>
            <p className="text-xs font-sans font-semibold text-stone-600">{Math.floor(totalDuration / 60)}t {totalDuration % 60}m</p>
          </div>
        </div>
      )}

      {/* Cross-module suggestions */}
      {suggestions.length > 0 && (
        <div className="bg-blue-50 rounded-xl border border-blue-200/50 p-3 space-y-1">
          <p className="text-[10px] font-sans font-bold text-blue-700 uppercase">Suggestions fra andre modules</p>
          {suggestions.map((s, i) => (
            <p key={i} className="text-xs font-sans text-blue-600">• {s}</p>
          ))}
        </div>
      )}

      {/* Timeline events */}
      <div className="space-y-1.5">
        {events.map((event, idx) => {
          const catCfg = CATEGORIES[event.category] || CATEGORIES.custom;
          const isExpanded = expanded === idx;

          return (
            <div key={event.id} data-testid={`timeline-event-${idx}`}
              className={`border rounded-xl transition-all ${isExpanded ? 'border-primary/30 bg-white' : 'border-stone-200 bg-white'}`}>
              <div className="flex items-center gap-2 p-2.5 cursor-pointer" onClick={() => setExpanded(isExpanded ? null : idx)}>
                {/* Time */}
                <div className="w-12 text-center flex-shrink-0">
                  <p className="text-xs font-sans font-bold text-stone-700">{event.startTime || '--:--'}</p>
                </div>

                {/* Category dot */}
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${catCfg.color.split(' ')[0]}`} />

                {/* Label */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-sans font-medium text-stone-700 truncate">{event.label || 'New event'}</p>
                  <p className="text-[9px] font-sans text-stone-400">{event.duration} min {event.buffer > 0 ? `+ ${event.buffer} min buffer` : ''}</p>
                </div>

                {/* Move buttons */}
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <button type="button" onClick={e => { e.stopPropagation(); moveEvent(idx, -1); }} disabled={idx === 0}
                    className="p-1 text-stone-300 hover:text-stone-600 disabled:opacity-20 transition-colors">
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button type="button" onClick={e => { e.stopPropagation(); moveEvent(idx, 1); }} disabled={idx === events.length - 1}
                    className="p-1 text-stone-300 hover:text-stone-600 disabled:opacity-20 transition-colors">
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>

                {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-stone-400" /> : <ChevronDown className="w-3.5 h-3.5 text-stone-400" />}
              </div>

              {isExpanded && (
                <div className="px-3 pb-3 border-t border-stone-100 space-y-3 pt-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase">Hendelse</label>
                      <input value={event.label} onChange={e => updateEvent(idx, 'label', e.target.value)}
                        className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase">Time</label>
                      <input type="time" value={event.startTime || ''} onChange={e => updateEvent(idx, 'startTime', e.target.value)}
                        className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase">Varighet (min)</label>
                      <input type="number" value={event.duration} onChange={e => updateEvent(idx, 'duration', parseInt(e.target.value) || 0)} min={5} max={480}
                        className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase">Buffer (min)</label>
                      <input type="number" value={event.buffer} onChange={e => updateEvent(idx, 'buffer', parseInt(e.target.value) || 0)} min={0} max={60}
                        className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase">Kategori</label>
                      <select value={event.category} onChange={e => updateEvent(idx, 'category', e.target.value)}
                        className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none">
                        {Object.entries(CATEGORIES).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase">Responsible</label>
                      <input value={event.responsible || ''} onChange={e => updateEvent(idx, 'responsible', e.target.value)} placeholder="Who is responsible?"
                        className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase">Notes</label>
                    <input value={event.notes || ''} onChange={e => updateEvent(idx, 'notes', e.target.value)} placeholder="Evt. detaljer"
                      className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={event.locked || false} onChange={e => updateEvent(idx, 'locked', e.target.checked)}
                        className="rounded border-stone-300 text-primary" />
                      <span className="text-[10px] font-sans text-stone-500">Lock time (don't recalculate)</span>
                    </label>
                    <button type="button" onClick={() => removeEvent(idx)} className="text-[10px] font-sans text-red-400 hover:text-red-600 flex items-center gap-1 transition-colors">
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add event */}
      <button type="button" onClick={addEvent} data-testid="add-timeline-event-btn"
        className="w-full border-2 border-dashed border-stone-200 rounded-xl p-3 flex items-center justify-center gap-2 text-stone-400 hover:border-stone-300 hover:text-stone-500 transition-all">
        <Plus className="w-4 h-4" />
        <span className="text-xs font-sans font-medium">Add event</span>
      </button>

      {events.length === 0 && (
        <div className="bg-white rounded-2xl border border-stone-100 p-8 text-center">
          <Clock className="w-8 h-8 text-primary mx-auto mb-3" />
          <h4 className="font-sans font-semibold text-stone-700 text-sm mb-1">No timeline yet</h4>
          <p className="text-xs font-sans text-stone-400 mb-4 max-w-sm mx-auto">
            Click "Auto-generate timeline" to create a complete schedule based on your wedding information.
          </p>
        </div>
      )}
    </div>
  );
};
